import React, { useState, useEffect, useMemo } from "react";
import { Box, Card, CardContent, Typography, Grid, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, CircularProgress } from "@mui/material";
import OutputIcon from "@mui/icons-material/Output";
import ScaleIcon from "@mui/icons-material/Scale";
import PlaceIcon from "@mui/icons-material/Place";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import KpiCard from "./KpiCard";
import StatusBadge from "./StatusBadge";
import ReportFilters from "./ReportFilters";
import ExportButtons from "./ExportButtons";
import { exportToPDF, exportToExcel } from "../../../utils/exportUtils";
import RelatorioDistribuidorService from "../../../service/relatorio_distribuidor_service";
import { toLocalISODate } from "../../../utils/dateUtils";

const operacaoLabels = {
	repasse: "Repasse",
	consolidacao: "Consolidação",
	transformacao: "Transformação",
};

function getDefaultDates() {
	const hoje = new Date();
	const inicio = new Date(hoje);
	inicio.setDate(inicio.getDate() - 30);
	return { from: toLocalISODate(inicio), to: toLocalISODate(hoje) };
}

function ReportSaidas() {
	const defaults = getDefaultDates();
	const [search, setSearch] = useState("");
	const [tipoFilter, setTipoFilter] = useState("todos");
	const [destinoFilter, setDestinoFilter] = useState("todos");
	const [dateFrom, setDateFrom] = useState(defaults.from);
	const [dateTo, setDateTo] = useState(defaults.to);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(null);
	const [data, setData] = useState(null);

	useEffect(() => {
		loadData();
	}, [dateFrom, dateTo, destinoFilter, search]); // eslint-disable-line react-hooks/exhaustive-deps

	const loadData = async () => {
		try {
			setLoading(true);
			setError(null);
			const service = new RelatorioDistribuidorService();
			const result = await service.fetchSaidas(dateFrom, dateTo, destinoFilter, search);
			setData(result);
		} catch (err) {
			setError(err.message);
		} finally {
			setLoading(false);
		}
	};

	const destinos = data?.destinos ?? [];

	const filtered = useMemo(() => {
		const allSaidas = data?.saidas ?? [];
		if (tipoFilter === "todos") return allSaidas;
		return allSaidas.filter((s) => s.tipoOperacao === tipoFilter);
	}, [data, tipoFilter]);

	const totalKg = filtered.reduce((s, e) => s + e.pesoKg, 0);
	const totalItens = filtered.length;
	const destinosUnicos = new Set(filtered.map((s) => s.destino)).size;
	const operacoes = filtered.reduce((acc, s) => { acc[s.tipoOperacao] = (acc[s.tipoOperacao] || 0) + 1; return acc; }, {});
	const mainOp = Object.entries(operacoes).sort((a, b) => b[1] - a[1])[0];

	const getExportConfig = () => ({
		title: "Relatório de Saídas de Produtos",
		subtitle: [
			`Período: ${new Date(dateFrom + "T12:00:00").toLocaleDateString("pt-BR")} a ${new Date(dateTo + "T12:00:00").toLocaleDateString("pt-BR")}`,
			destinoFilter !== "todos" ? `Destino: ${destinos.find(d => d.id === destinoFilter)?.nome}` : "",
		].filter(Boolean).join(" | "),
		filename: `saidas-${dateFrom}-a-${dateTo}`,
		kpis: [
			{ label: "Total Saídas", value: String(totalItens) },
			{ label: "Peso Expedido", value: `${totalKg.toLocaleString("pt-BR")} kg` },
			{ label: "Destinos", value: String(destinosUnicos) },
			{ label: "Op. Principal", value: mainOp ? operacaoLabels[mainOp[0]] || mainOp[0] : "-" },
		],
		headers: ["Data", "Destino", "Cidade", "Produto", "Lote Consolidado", "Qtd", "Unidade", "Peso (kg)", "NF", "Operação"],
		rows: filtered.map((s) => [
			new Date(s.data + "T12:00:00").toLocaleDateString("pt-BR"),
			s.destino, s.cidade, s.produto, s.loteConsolidado,
			s.quantidade, s.unidade, s.pesoKg, s.notaFiscal,
			operacaoLabels[s.tipoOperacao] || s.tipoOperacao,
		]),
	});

	return (
		<Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
			<Grid container spacing={2}>
				<Grid size={{ xs: 6, md: 3 }}>
					<KpiCard icon={<OutputIcon />} title="Total Saídas" value={String(totalItens)} subtitle="expedições no período" />
				</Grid>
				<Grid size={{ xs: 6, md: 3 }}>
					<KpiCard icon={<ScaleIcon />} title="Peso Expedido" value={`${totalKg.toLocaleString("pt-BR")} kg`} />
				</Grid>
				<Grid size={{ xs: 6, md: 3 }}>
					<KpiCard icon={<PlaceIcon />} title="Destinos" value={String(destinosUnicos)} subtitle="pontos de entrega" />
				</Grid>
				<Grid size={{ xs: 6, md: 3 }}>
					<KpiCard icon={<SwapHorizIcon />} title="Op. Principal" value={mainOp ? mainOp[0].charAt(0).toUpperCase() + mainOp[0].slice(1) : "-"} subtitle={mainOp ? `${mainOp[1]} operações` : ""} />
				</Grid>
			</Grid>

			<Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", gap: 1.5 }}>
				<ReportFilters
					searchValue={search}
					onSearchChange={setSearch}
					searchPlaceholder="Produto, destino ou lote..."
					dateFrom={dateFrom}
					dateTo={dateTo}
					onDateFromChange={setDateFrom}
					onDateToChange={setDateTo}
					extraFilters={[
						{
							label: "Tipo",
							value: tipoFilter,
							onChange: setTipoFilter,
							options: [
								{ value: "repasse", label: "Repasse" },
								{ value: "consolidacao", label: "Consolidação" },
								{ value: "transformacao", label: "Transformação" },
							],
						},
						{
							label: "Destino",
							value: destinoFilter,
							onChange: setDestinoFilter,
							options: destinos.map((d) => ({ value: d.id, label: d.nome })),
						},
					]}
				/>
				<ExportButtons onExportPDF={() => exportToPDF(getExportConfig())} onExportExcel={() => exportToExcel(getExportConfig())} />
			</Box>

			<Card sx={{ borderRadius: "16px", boxShadow: "0 1px 3px rgba(0,0,0,0.05)", border: "1px solid #dfe3e8" }}>
				<CardContent sx={{ p: 0 }}>
					<Typography variant="subtitle1" sx={{ fontWeight: 600, color: "#0f4557", fontSize: "0.9rem", px: 2.5, pt: 2, pb: 1.5 }}>
						Saídas de Produtos
					</Typography>
					{loading ? (
						<Box sx={{ display: "flex", justifyContent: "center", py: 4, gap: 1, color: "#8e9bae" }}>
							<CircularProgress size={18} /> <Typography variant="body2">Carregando...</Typography>
						</Box>
					) : error ? (
						<Box sx={{ py: 4, textAlign: "center", color: "#dc2626" }}><Typography variant="body2">Erro ao carregar dados</Typography></Box>
					) : (
						<TableContainer>
							<Table size="small">
								<TableHead>
									<TableRow>
										<TableCell sx={{ fontWeight: 600, color: "#8e9bae", fontSize: "0.75rem" }}>Data</TableCell>
										<TableCell sx={{ fontWeight: 600, color: "#8e9bae", fontSize: "0.75rem" }}>Destino</TableCell>
										<TableCell sx={{ fontWeight: 600, color: "#8e9bae", fontSize: "0.75rem" }}>Produto</TableCell>
										<TableCell sx={{ fontWeight: 600, color: "#8e9bae", fontSize: "0.75rem" }}>Lote Consolidado</TableCell>
										<TableCell sx={{ fontWeight: 600, color: "#8e9bae", fontSize: "0.75rem" }} align="right">Qtd</TableCell>
										<TableCell sx={{ fontWeight: 600, color: "#8e9bae", fontSize: "0.75rem" }} align="right">Peso (kg)</TableCell>
										<TableCell sx={{ fontWeight: 600, color: "#8e9bae", fontSize: "0.75rem" }}>NF</TableCell>
										<TableCell sx={{ fontWeight: 600, color: "#8e9bae", fontSize: "0.75rem" }}>Operação</TableCell>
									</TableRow>
								</TableHead>
								<TableBody>
									{filtered.map((s) => (
										<TableRow key={s.id}>
											<TableCell sx={{ fontSize: "0.8rem" }}>{new Date(s.data + "T12:00:00").toLocaleDateString("pt-BR")}</TableCell>
											<TableCell>
												<Typography variant="body2" sx={{ fontSize: "0.8rem" }}>{s.destino}</Typography>
												<Typography variant="caption" sx={{ color: "#8e9bae", fontSize: "0.7rem" }}>{s.cidade}</Typography>
											</TableCell>
											<TableCell sx={{ fontSize: "0.8rem" }}>{s.produto}</TableCell>
											<TableCell sx={{ fontFamily: "monospace", fontSize: "0.75rem" }}>{s.loteConsolidado}</TableCell>
											<TableCell align="right" sx={{ fontSize: "0.8rem" }}>{s.quantidade} {s.unidade}</TableCell>
											<TableCell align="right" sx={{ fontSize: "0.8rem", fontWeight: 500 }}>{s.pesoKg.toLocaleString("pt-BR")}</TableCell>
											<TableCell sx={{ fontFamily: "monospace", fontSize: "0.75rem" }}>{s.notaFiscal}</TableCell>
											<TableCell><StatusBadge status={s.tipoOperacao} /></TableCell>
										</TableRow>
									))}
									{filtered.length === 0 && (
										<TableRow>
											<TableCell colSpan={8} sx={{ textAlign: "center", color: "#8e9bae", py: 4 }}>Nenhum registro encontrado</TableCell>
										</TableRow>
									)}
								</TableBody>
							</Table>
						</TableContainer>
					)}
				</CardContent>
			</Card>
		</Box>
	);
}

export default ReportSaidas;
